import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { z } from "zod";

import { BadgeSelector } from "@/components/fields/badge-selector";
import { RemoteSelect } from "@/components/fields/remote-select";
import { useGenreRepository } from "@/pages/genres/hooks/use-genre-repository";
import { GenreNestedType } from "@/pages/genres/schemas/genre.nested.form";

interface GenresFieldProps extends AutoFormFieldProps {}

const POPULAR_GENRES = [
  {
    id: "afrobeat",
    name: "afrobeat",
  },
  {
    id: "batchata",
    name: "batchata",
  },
  {
    id: "blues",
    name: "blues",
  },
  {
    id: "disco",
    name: "disco",
  },
  {
    id: "drum_and_bass",
    name: "drum and bass",
  },
  {
    id: "edm",
    name: "edm",
  },
  {
    id: "electro",
    name: "electro",
  },
  {
    id: "funk",
    name: "funk",
  },
  {
    id: "hip_hop",
    name: "hip hop",
  },
  {
    id: "house",
    name: "house",
  },
  {
    id: "jazz",
    name: "jazz",
  },
  {
    id: "latin",
    name: "latin",
  },
  {
    id: "metal",
    name: "metal",
  },
  {
    id: "pop",
    name: "pop",
  },
  {
    id: "punk",
    name: "punk",
  },
  {
    id: "reggae",
    name: "reggae",
  },
  {
    id: "reggaeton",
    name: "reggaeton",
  },
  {
    id: "rb",
    name: "r&b",
  },
  {
    id: "rock",
    name: "rock",
  },
  {
    id: "rock_and_roll",
    name: "rock and roll",
  },
  {
    id: "salsa",
    name: "salsa",
  },
  {
    id: "soul",
    name: "soul",
  },
  {
    id: "techno",
    name: "techno",
  },
  {
    id: "trap",
    name: "trap",
  },
];

const GenresField = (props: GenresFieldProps) => {
  const { field, inputProps } = props;
  const { search: searchGenres } = useGenreRepository();
  const formContext = useFormContext();
  const value = useWatch({
    control: formContext.control,
    name: field.key,
  });

  const addGenre = (value: GenreNestedType | undefined) => {
    if (!value) return;
    const newValues = formContext.getValues(field.key) || [];
    if (newValues.find((v: any) => v.id === value.id)) {
      return;
    }
    newValues.push(value);
    formContext.setValue(field.key, newValues);
  };

  const removeGenre = (id: string) => {
    const newValues = formContext.getValues(field.key) || [];
    formContext.setValue(
      field.key,
      newValues.filter((v: GenreNestedType) => v.id !== id),
    );
  };

  const onChange = (values: Array<GenreNestedType>) => {
    formContext.setValue(field.key, values);
  };

  return (
    <div className="flex flex-col space-y-2 text-foreground w-full">
      <RemoteSelect<GenreNestedType>
        className="w-[500px]"
        triggerType="change"
        onValueChanged={addGenre}
        accessorFn={(option) => ({
          id: option?.id || option?.name,
          name: option ? `${option?.name}` : "",
        })}
        onSearch={async (query, onStatusChanged) => {
          if (!query) {
            return [];
          }
          onStatusChanged("loading");
          const genres = await searchGenres(query);
          onStatusChanged("success");
          return genres;
        }}
      />
      <div className="flex flex-row space-x-2 flex-wrap">
        {POPULAR_GENRES.map((g) => (
          <BadgeSelector
            key={g.id}
            item={g}
            values={value ?? []}
            onChange={onChange}
          />
        ))}
        {value?.length > 0 &&
          value
            .filter(
              (g: any) =>
                g?.id && !POPULAR_GENRES.map((g) => g.id).includes(g?.id),
            )
            .map((g: any) => (
              <BadgeSelector
                key={g.id}
                item={g}
                values={value ?? []}
                onChange={onChange}
              />
            ))}
      </div>
    </div>
  );
};

export default GenresField;
